import type {
  ChartPoint,
  ProgressChartModel,
  ThisWeekRadarAxis,
} from '../types'
import { formatStatValue } from './statValues'

/** The games-played line plots the game count itself. */
const GAMES_ABBR = 'GP'

/** "3 Games Played" / "1 Game Played" — spelled out, never "GP". */
export const formatGamesPlayed = (games: number): string =>
  `${games} ${games === 1 ? 'Game' : 'Games'} Played`

/** "Goals: 5" — a stat's name (or abbr) and its value. */
export const formatStatLine = (label: string, value: string): string =>
  `${label}: ${value}`

export interface PointTooltip {
  /** The period, e.g. "Feb '26 · 3 Games Played". */
  title: string
  /** The hovered line's real value, e.g. "Goals: 5". */
  value: string
}

/**
 * Hover text for one point on a progress line. Always the real value — on a
 * chart of several normalized lines the plotted height means nothing.
 */
export const buildPointTooltip = (
  point: ChartPoint,
  abbr: string,
  name?: string
): PointTooltip => {
  if (abbr === GAMES_ABBR) {
    return {
      title: point.label,
      value: formatGamesPlayed(point.gamesPlayed),
    }
  }
  return {
    title: `${point.label} · ${formatGamesPlayed(point.gamesPlayed)}`,
    value: formatStatLine(name ?? abbr, formatStatValue(point.value, abbr)),
  }
}

/** Hover text for one This Week radar point: the week and the spoke's total. */
export const buildRadarPointTooltip = (
  axis: ThisWeekRadarAxis,
  gamesPlayed: number
): PointTooltip => ({
  title: `This Week · ${formatGamesPlayed(gamesPlayed)}`,
  value: formatStatLine(axis.name, axis.value),
})

/** One charted line, as the readout needs it. */
export interface ReadoutSeries {
  abbr: string
  /** Full stat name; the abbr is used without it. */
  name?: string
  chart: ProgressChartModel
}

export interface ChartReadout {
  /** e.g. "Feb '26 · 3 Games Played". */
  period: string
  /** One entry per line in selection order, e.g. "Goals: 5"; [] on the games-played line. */
  values: { abbr: string; text: string }[]
}

/** "Feb '26 · 3 Games Played". */
export const formatChartPeriod = (point: ChartPoint): string =>
  `${point.label} · ${formatGamesPlayed(point.gamesPlayed)}`

/**
 * The selected period across every line — always the real values, never the
 * normalized heights. The games-played line is just the period.
 */
export const buildChartReadout = (
  point: ChartPoint,
  series: ReadoutSeries[]
): ChartReadout => {
  const isGamesLine = series.length === 1 && series[0]?.abbr === GAMES_ABBR
  return {
    period: formatChartPeriod(point),
    values: isGamesLine
      ? []
      : series.map((s) => ({
          abbr: s.abbr,
          text: formatStatLine(
            s.name ?? s.abbr,
            formatStatValue(s.chart.points[point.index]?.value ?? null, s.abbr)
          ),
        })),
  }
}

/** `<period> · <Stat name>: <value> · …` on one line. */
export const formatChartReadout = (readout: ChartReadout): string =>
  [readout.period, ...readout.values.map((v) => v.text)].join(' · ')
